// @flow
import _ from 'lodash';
import window from 'window';
import ko from 'rgs-knockout';
import keycode from 'keycode';

import type { Disposable, Observable, PureComputed, ComponentInfo } from 'rgs-knockout/types';

// rgs-nfd-ui-kit
import { BaseComponent, withProps } from '../helpers';
import {
	DEFAULT_DROPDOWN_pathToName,
	DEFAULT_DROPDOWN_pathToID,
} from './contracts';

type DropdownState = {
	isOpened: boolean,
};

export type TDropdownBtnOnCancel = {
	visible: boolean | Observable<boolean>,
	onClick: () => *,
}

export type DropdownOptions = {
	activeValue: PureComputed<string | number> | Observable<string> | string,
	isOpened: Observable<boolean>,
	pathToName?: string,
	pathToID?: string,
	state?: PureComputed<DropdownState> | Observable<DropdownState>,
	placeholder?: any,
	onCancel?: TDropdownBtnOnCancel,
	validationValue?: Observable<any> | PureComputed<any>,
}

/*
 * @class Dropdown
 * @classdesc
 * Usage: <nfd-dropdown params="props: dropdownOptions">content</nfd-dropdown>
 */
export class Dropdown {
	ref: Element;
	isOpened: Observable<boolean>;
	onDropdownToggle: (value: boolean) => any;
	activeModelValue: PureComputed<?string>;
	props: {
		placeholder: BaseComponent;
		isOpened: Observable<boolean>;
	};
	validationValue: Observable<any> | PureComputed<any>;
	onToggleSubscription: Disposable;
	onStateSubscription: Disposable | { dispose: Function };

	constructor (options: DropdownOptions, ref: ComponentInfo) {
		const pathToName = options.pathToName || DEFAULT_DROPDOWN_pathToName;
		const pathToID = options.pathToID || DEFAULT_DROPDOWN_pathToID;

		const params = _.defaultsDeep({}, withProps(options), {
			pathToName,
			pathToID,
			placeholder: {},
			activeValue: '',
			validationValue: ko.observable(),
			onDropdownToggle: _.noop,
			state: { subscribe: _.noop },
			isOpened: ko.observable(false),
			isDisabled: ko.observable(false),

			onCancel: {
				css: {},
				visible: false,
				onClick: _.noop,
			},
		});

		this.validationValue = params.validationValue;

		this.ref = ref.element;
		this.isOpened = params.isOpened.extend({throttle: 100});
		this.isDisabled = params.isDisabled;
		this.onDropdownToggle = params.onDropdownToggle;

		this.onStateSubscription = params.state.subscribe(this._setProps) || { dispose: _.noop };
		this.onToggleSubscription = this.isOpened.subscribe(this.handleDropdownToggle, this);

		this.activeModelValue = ko.pureComputed(() => ko.unwrap(params.activeValue));

		this.props = {
			isOpened: this.isOpened,
			isDisabled: this.isDisabled,
			validationValue: this.validationValue,
			onCancel: this._configureBtnOnCancel(params.onCancel),
			onLayoutClick: this.handleClickOnLayout,
			placeholder: new BaseComponent({
				...params.placeholder,
				css: {
					'nfd-dropdown-title-pure-placeholder':
						ko.pureComputed(() => !this.activeModelValue()),
				},
			}),
		};

		window.addEventListener('click', this._onHideIfOuterClick, true);
		window.addEventListener('keydown', this.handleKeyDown);
	}

	closeDropdown (): void {
		if (this.props.isOpened.peek()) {
			this.props.isOpened(false);
		}
	}

	_configureBtnOnCancel (params: TDropdownBtnOnCancel) {
		const isBtnCancelVisible = ko.pureComputed(() =>
			ko.unwrap(params.visible) && this.activeModelValue(),
		);

		return {
			...params,
			visible: isBtnCancelVisible,
		};
	}

	handleKeyDown = (e: Event) => {
		if (e.which === keycode('escape') || e.which === keycode('enter')) {
			this.closeDropdown();
		}

		return true; // must be!
	};

	handleDropdownToggle (isOpened: boolean): void {
		this.onDropdownToggle(isOpened);
	}

	handleClickOnLayout = () => {
		if (!this.isDisabled()) {
			this.props.isOpened(!this.props.isOpened.peek());
		}
	};

	_setProps = (state: DropdownState): void => {
		const { isOpened } = state || {};

		this.props.isOpened(isOpened);
	};

	_onHideIfOuterClick = ({ target }: { target: Node }): void => {
		if (document.body && !document.body.contains(this.ref)) { // eslint-disable-line no-undef
			ko.cleanNode(this.ref);
			return;
		}
		if (this.props.isOpened.peek() && !this.ref.contains(target)) {
			this.props.isOpened(false);
		}
	};

	dispose (): void {
		window.removeEventListener('click', this._onHideIfOuterClick);
		window.removeEventListener('keydown', this.handleKeyDown);

		this.onToggleSubscription.dispose();
		this.onStateSubscription.dispose();
	}
}
